<template>
  <table class="full-width-table diary-table">
    <tbody>
      <tr class="table-header">
        <th style="width: 5%">Date</th>
        <th style="width: 8%">Created By</th>
        <th v-if="type === 'company'" style="width: 8%">Contact</th>
        <th style="width: 5%">Type</th>
        <th style="width: 10%">Project</th>
        <th style="width: auto">Note</th>
        <th style="width: 6rem"></th>
      </tr>
      <tr v-if="!loading && diaryEntries.length > 0" v-for="entry in diaryEntries" :key="entry.DIARYID" @click="editDiaryEntry(entry)" class="diary-entry-row">
        <td>
          <span>{{ formatDate(entry.CREATEDATE) }}</span>
        </td>
        <td>
          <span>{{ getDisplayName(entry.ASSIGNEDID) }}</span>
        </td>
        <td v-if="type === 'company'">
          <span v-if="entry.contact" class="hover-link" @click.stop="navigateToContact(entry.contact)">
            {{ getContactName(entry.contact) }}
          </span>
        </td>
        <td>
          <span>{{ entry.TYPE }}</span>
        </td>
        <td>
          <span>{{ entry.PROJECT }}</span>
        </td>
        <td class="note-cell">
          <span class="note-text">{{ entry.DIARYTEXT }}</span>
        </td>
        <td>
          <div class="actions-container" @click.stop>
            <div class="icon-button small-icon" @click="emailEntry(entry)" title="Email">
              <img id="noHover" src="../assets/email-bubble.svg" alt="Email" />
              <img id="hover" src="../assets/email-bubble-hover.svg" alt="Email" />
            </div>
            <div class="icon-button small-icon" @click="confirmDeleteEntry(entry)" title="Delete" v-if="user.contactsAdmin || entry.ASSIGNEDID === user.userid">
              <img id="noHover" src="../assets/trash-bubble.svg" alt="Delete" />
              <img id="hover" src="../assets/trash-bubble-hover.svg" alt="Delete" />
            </div>
          </div>
        </td>
      </tr>
      <tr v-if="!loading && diaryEntries.length === 0" class="no-results">
        <td :colspan="type === 'company' ? 7 : 6">No diary notes found</td>
      </tr>
      <tr v-if="loading" class="loading-row">
        <td :colspan="type === 'company' ? 7 : 6"><img src="../assets/spinner.svg" /></td>
      </tr>
    </tbody>
  </table>

  <!-- Edit Diary Note Popup -->
  <EditDiaryNote v-if="showEditDiaryNote" :diary-entry="selectedEntry" :entity-type="type" :entity-id="entityid" :contacts="contacts" @closePopup="closeEditDiaryNote" @save="executeQuery" />

  <!-- Delete Confirmation -->
  <Confirmation
    v-if="showDeleteConfirm"
    @confirm="handleDeleteEntry"
    @cancel="showDeleteConfirm = false"
    header="Delete Diary Entry"
    message="This will permanently delete this diary entry. Are you sure?"
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
</template>

<script>
import { ref, computed, watch } from 'vue';
import useAlerts from '@/composables/useAlerts';
import useDiaryQuery from '@/composables/useDiaryQuery';
import useDiaryMutate from '@/composables/useDiaryMutate';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useUsersQuery from '@/composables/useUsersQuery';
import useCurrentUser from '@/composables/useCurrentUser';
import Confirmation from './Confirmation.vue';
import EditDiaryNote from './EditDiaryNote.vue';

export default {
  name: 'DiaryTab',
  components: {
    Confirmation,
    EditDiaryNote,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: true,
    },
    contacts: {
      type: Array,
      required: false,
    },
  },
  setup(props) {
    const { addAlert } = useAlerts();
    const queryDiaries = useDiaryQuery();
    const { deleteDiary } = useDiaryMutate();
    const { user } = useCurrentUser();
    const { decryptSymbols } = useDecryptSymbols();
    const { allActiveUsers, getUsers } = useUsersQuery();

    getUsers();

    // Reactive refs
    const diaryEntries = ref([]);
    const showDeleteConfirm = ref(false);
    const selectedEntry = ref(null);
    const diaryQuery = ref(null);

    // Computed properties
    const loading = computed(() => {
      return diaryQuery.value?.loading || false;
    });

    const error = computed(() => {
      return diaryQuery.value?.error || null;
    });

    // Execute query function
    const executeQuery = () => {
      if (!props.entityid || props.entityid === 'new') {
        diaryQuery.value = null;
        diaryEntries.value = [];
        return;
      }

      // Set up query parameters based on type
      const queryParams = {};
      if (props.type === 'company') {
        queryParams.companyid = props.entityid;
      } else if (props.type === 'contact') {
        queryParams.contactid = props.entityid;
      }

      diaryQuery.value = queryDiaries(queryParams);
    };

    // Watch for props changes
    watch(() => [props.type, props.entityid], executeQuery, { immediate: true });

    // Watch for query result changes and update diaryEntries
    watch(
      () => diaryQuery.value?.result?.diaries,
      (newResult) => {
        if (newResult) {
          const decryptedEntries = decryptSymbols(newResult);
          // Sort by date created, newest first
          diaryEntries.value = decryptedEntries.sort((a, b) => {
            // Try to parse as integer timestamp first
            const timestampA = parseInt(a.CREATEDATE);
            const timestampB = parseInt(b.CREATEDATE);

            if (!isNaN(timestampA) && !isNaN(timestampB)) {
              return timestampB - timestampA; // Descending order (newest first)
            }

            // Fall back to date parsing
            const dateA = new Date(a.CREATEDATE);
            const dateB = new Date(b.CREATEDATE);
            return dateB - dateA; // Descending order (newest first)
          });
        } else {
          diaryEntries.value = [];
        }
      },
      { immediate: true, deep: true }
    );

    return {
      loading,
      error,
      diaryEntries,
      showDeleteConfirm,
      selectedEntry,
      addAlert,
      executeQuery,
      allActiveUsers,
      deleteDiary,
      user,
    };
  },
  data() {
    return {
      showEditDiaryNote: false,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';

      try {
        // Try to parse as integer timestamp first
        const timestamp = parseInt(dateString);
        if (!isNaN(timestamp)) {
          const date = new Date(timestamp);
          return date.toLocaleDateString('en-US');
        }

        // Fall back to string parsing
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US');
      } catch (error) {
        console.error('Error parsing date:', error);
        return '';
      }
    },
    getContactName(contact) {
      if (!contact) return '';
      return `${contact.FIRSTNAME || ''} ${contact.LASTNAME || ''}`.trim();
    },
    getDisplayName(assignedId) {
      if (!assignedId) return 'Unknown';

      const user = this.allActiveUsers.find((user) => user.USERID === assignedId);
      return user?.DISPLAYNAME || 'Unknown';
    },
    navigateToContact(contact) {
      if (contact && contact.CONTACTID) {
        this.$router.push({ name: 'contacts', params: { contactid: contact.CONTACTID } });
      }
    },
    emailEntry(entry) {
      // Format date as MM/DD/YYYY
      const formatDateForEmail = (dateString) => {
        if (!dateString) return '';

        try {
          // Try to parse as integer timestamp first
          const timestamp = parseInt(dateString);
          if (!isNaN(timestamp)) {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-US');
          }

          // Fall back to string parsing
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US');
        } catch (error) {
          return '';
        }
      };

      // Get entity name based on type
      let entityName = '';
      if (this.type === 'company') {
        entityName = entry.company?.NAME || 'Company';
      } else if (this.type === 'contact') {
        entityName = entry.contact ? this.getContactName(entry.contact) : 'Contact';
      }

      // Create mailto link with simplified format
      const formattedDate = formatDateForEmail(entry.CREATEDATE);
      const subject = encodeURIComponent(`Diary Note - ${formattedDate}, ${entityName}`);
      const body = encodeURIComponent(entry.DIARYTEXT || '');

      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    confirmDeleteEntry(entry) {
      this.selectedEntry = entry;
      this.showDeleteConfirm = true;
    },
    async handleDeleteEntry() {
      this.showDeleteConfirm = false;

      if (!this.selectedEntry) return;

      try {
        await this.deleteDiary(this.selectedEntry.DIARYID);
        this.addAlert('Diary entry deleted successfully', 'low', 2);
        // Refresh the diary entries after successful deletion
        this.executeQuery();
      } catch (error) {
        // Error handling is done in the composable
      } finally {
        this.selectedEntry = null;
      }
    },
    openAddDiaryNote() {
      this.selectedEntry = null;
      this.showEditDiaryNote = true;
    },
    editDiaryEntry(entry) {
      this.selectedEntry = entry;
      this.showEditDiaryNote = true;
    },
    closeEditDiaryNote() {
      this.showEditDiaryNote = false;
      this.selectedEntry = null;
    },
    async handleSaveDiaryNote(updatedEntry) {
      try {
        if (updatedEntry.isEdit) {
          // Edit existing entry
          const index = this.diaryEntries.findIndex((entry) => entry.DIARYID === updatedEntry.DIARYID);
          if (index > -1) {
            Object.assign(this.diaryEntries[index], updatedEntry);
          }
        } else {
          // Add new entry
          this.diaryEntries.unshift(updatedEntry);
        }
      } catch (error) {
        console.error('Error updating diary entries:', error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.diary-table {
  th:first-child,
  td:first-child {
    padding-left: 3.75rem;
  }

  td {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .loading-row {
    td {
      background: white;
    }
  }
}

.hover-link {
  cursor: pointer !important;

  &:hover {
    text-decoration: underline;
  }
}

.note-cell {
  .note-text {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.actions-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-right: 1rem;
}
</style>
